import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { setMongoUser, setTutorialActiveStepIndex } from '../store/authSlice';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { apiService } from '../services/apiService';
import {
  getStepsForRole,
  TutorialStep,
} from '../components/tutorial/tutorialSteps';
import { tutorialConfig } from '../config';

interface TutorialContextValue {
  isRunning: boolean;
  isTutorialCompleted: boolean;
  isLoading: boolean;
  isTutorialInProgress: boolean;
  startTutorial: () => void;
  stopTutorial: () => void;
  completeTutorial: () => Promise<void>;
  resetTutorial: () => Promise<void>;
}

const TutorialContext = createContext<TutorialContextValue | undefined>(undefined);

// Helper to get page path
const getPagePath = (page: string): string => {
  const pageToPath: Record<string, string> = {
    'dashboard': '/dashboard',
    'knowledge-base': '/knowledge-base',
    'training': '/training',
    'training-builder': '/training/builder',
    'calendar': '/calendar',
    'settings': '/settings',
  };
  return pageToPath[page] || '/dashboard';
};

// Helper to get current page from path
const getCurrentPage = (pathname: string): string => {
  if (pathname.startsWith('/training/builder')) return 'training-builder';
  if (pathname.startsWith('/training')) return 'training';
  if (pathname.startsWith('/knowledge-base')) return 'knowledge-base';
  if (pathname.startsWith('/calendar')) return 'calendar';
  if (pathname.startsWith('/settings')) return 'settings';
  return 'dashboard';
};

export const TutorialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { mongoUser } = useAppSelector((state) => state.auth);
  const { isAdmin, isSuperAdmin } = useRoleAccess();

  const [isRunning, setIsRunning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const tourRef = useRef<Shepherd.Tour | null>(null);
  const allStepsRef = useRef<TutorialStep[]>([]);
  const currentStepIndexRef = useRef(0);
  const isNavigatingRef = useRef(false);
  const pendingStepIndexRef = useRef<number | null>(null);
  const hasCreatedTourRef = useRef(false);

  const isTutorialCompleted = mongoUser?.preferences?.tutorialCompleted ?? false;

  // Complete tutorial and persist to backend
  const completeTutorial = useCallback(async () => {
    console.log('[Tutorial] completeTutorial called');
    setIsLoading(true);
    try {
      // Destroy tour without triggering cancel handlers
      if (tourRef.current) {
        tourRef.current.hide();
        tourRef.current = null;
      }

      await apiService.put('/auth/profile', {
        preferences: { tutorialCompleted: true }
      });

      if (mongoUser) {
        dispatch(setMongoUser({
          ...mongoUser,
          preferences: mongoUser.preferences
            ? { ...mongoUser.preferences, tutorialCompleted: true }
            : { theme: 'light' as const, notifications: { email: true, push: true }, tutorialCompleted: true }
        }));
      }

      setIsRunning(false);
      currentStepIndexRef.current = 0;
      isNavigatingRef.current = false;
      pendingStepIndexRef.current = null;
      // Clear Redux tutorial state
      dispatch(setTutorialActiveStepIndex(null));
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save tutorial completion:', error);
    } finally {
      setIsLoading(false);
    }
  }, [mongoUser, dispatch, navigate]);

  // Stop tutorial without completing (just hide, don't save)
  const stopTutorial = useCallback(() => {
    console.log('[Tutorial] stopTutorial called');
    if (tourRef.current) {
      tourRef.current.hide();
      tourRef.current = null;
    }
    setIsRunning(false);
    currentStepIndexRef.current = 0;
    isNavigatingRef.current = false;
    pendingStepIndexRef.current = null;
    // Clear Redux tutorial state
    dispatch(setTutorialActiveStepIndex(null));
  }, [dispatch]);

  // Create and start tour from a given step index
  const createAndStartTour = useCallback((startIndex: number) => {
    // Prevent duplicate calls
    if (hasCreatedTourRef.current) {
      console.log('[Tutorial] Skipping duplicate createAndStartTour call');
      return;
    }

    const allSteps = allStepsRef.current;
    const totalSteps = allSteps.length;

    if (startIndex >= totalSteps) {
      completeTutorial();
      return;
    }

    console.log('[Tutorial] Creating tour starting at step:', startIndex);
    hasCreatedTourRef.current = true;

    // Clean up existing tour (just hide, don't cancel)
    if (tourRef.current) {
      tourRef.current.hide();
      tourRef.current = null;
    }

    // Create new tour
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: 'shepherd-theme-custom',
        scrollTo: { behavior: 'smooth', block: 'center' },
        cancelIcon: { enabled: true },
      },
    });

    // Store references for use in button actions
    const navRef = isNavigatingRef;
    const pendingRef = pendingStepIndexRef;
    const stepIndexRef = currentStepIndexRef;
    const tourReference = { current: tour };
    const stepsRef = allStepsRef;

    // Add all steps to the tour
    allSteps.forEach((step, index) => {
      const isFirstStep = index === 0;
      const isLastStep = index === totalSteps - 1;

      // Handle clickSelector before showing step
      const beforeShowPromise = step.clickSelector
        ? () => new Promise<void>((resolve) => {
            const el = document.querySelector(step.clickSelector!) as HTMLElement;
            if (el) el.click();
            setTimeout(resolve, 150);
          })
        : undefined;

      tour.addStep({
        id: `step-${index}`,
        title: step.popover?.title,
        text: `<p>${step.popover?.description || ''}</p><p class="shepherd-progress">${index + 1} of ${totalSteps}</p>`,
        attachTo: step.element ? { element: step.element, on: (step.popover?.side || 'bottom') as 'top' | 'bottom' | 'left' | 'right' } : undefined,
        beforeShowPromise,
        buttons: [
          // Back button
          {
            text: 'Back',
            classes: 'shepherd-button-secondary',
            disabled: isFirstStep,
            action: function() {
              if (index > 0) {
                const prevStep = stepsRef.current[index - 1];
                const prevPage = prevStep.page || 'dashboard';
                const currentPage = getCurrentPage(window.location.pathname);

                console.log('[Tutorial] Back clicked:', { index, prevPage, currentPage });

                if (prevPage !== currentPage) {
                  // Navigate to previous page
                  navRef.current = true;
                  pendingRef.current = index - 1;
                  stepIndexRef.current = index - 1;
                  hasCreatedTourRef.current = false; // Allow new tour creation
                  // Update Redux state before navigating
                  dispatch(setTutorialActiveStepIndex(index - 1));
                  tour.hide();
                  navigate(getPagePath(prevPage));
                } else {
                  stepIndexRef.current = index - 1;
                  tour.back();
                }
              }
            },
          },
          // Next button
          {
            text: isLastStep ? 'Finish Tour' : 'Next',
            classes: 'shepherd-button-primary',
            action: function() {
              if (isLastStep) {
                completeTutorial();
              } else {
                const nextStep = stepsRef.current[index + 1];
                const nextPage = nextStep.page || 'dashboard';
                const currentPage = getCurrentPage(window.location.pathname);

                console.log('[Tutorial] Next clicked:', { index, nextPage, currentPage });

                if (nextPage !== currentPage) {
                  // Navigate to next page
                  navRef.current = true;
                  pendingRef.current = index + 1;
                  stepIndexRef.current = index + 1;
                  hasCreatedTourRef.current = false; // Allow new tour creation
                  // Update Redux state before navigating
                  dispatch(setTutorialActiveStepIndex(index + 1));
                  tour.hide();
                  navigate(getPagePath(nextPage));
                } else {
                  stepIndexRef.current = index + 1;
                  tour.next();
                }
              }
            },
          },
        ],
        when: {
          show: () => {
            stepIndexRef.current = index;
            // Update Redux state when step is shown
            dispatch(setTutorialActiveStepIndex(index));
            console.log('[Tutorial] Showing step:', index, step.popover?.title);
          },
        },
      });
    });

    // Handle X button click - complete the tutorial
    tour.on('cancel', () => {
      console.log('[Tutorial] Tour cancelled (X clicked), isNavigating:', navRef.current);
      // Only complete if NOT navigating between pages
      if (!navRef.current) {
        completeTutorial();
      }
    });

    tourRef.current = tour;

    // Start the tour at the correct step
    tour.start();
    if (startIndex > 0) {
      tour.show(`step-${startIndex}`);
    }
  }, [completeTutorial, navigate]);

  // Start the tutorial
  const startTutorial = useCallback(() => {
    // Check if tutorial is disabled via config or for super admin users
    if (tutorialConfig.disabled) {
      console.log('[Tutorial] Tutorial disabled via config.tutorial.disabled');
      return;
    }
    if (isSuperAdmin) {
      console.log('[Tutorial] Tutorial disabled for super admin users');
      return;
    }

    const steps = getStepsForRole(isAdmin);
    allStepsRef.current = steps;
    currentStepIndexRef.current = 0;
    isNavigatingRef.current = false;
    pendingStepIndexRef.current = null;
    hasCreatedTourRef.current = false;

    console.log('[Tutorial] Starting tutorial with', steps.length, 'steps, isAdmin:', isAdmin);
    setIsRunning(true);
    // Set Redux tutorial state to step 0
    dispatch(setTutorialActiveStepIndex(0));

    if (location.pathname !== '/dashboard') {
      isNavigatingRef.current = true;
      pendingStepIndexRef.current = 0;
      navigate('/dashboard');
    } else {
      setTimeout(() => createAndStartTour(0), 100);
    }
  }, [isAdmin, location.pathname, navigate, createAndStartTour, dispatch]);

  // Reset tutorial for restart
  const resetTutorial = useCallback(async () => {
    setIsLoading(true);
    try {
      await apiService.put('/auth/profile', {
        preferences: { tutorialCompleted: false }
      });

      if (mongoUser) {
        dispatch(setMongoUser({
          ...mongoUser,
          preferences: mongoUser.preferences
            ? { ...mongoUser.preferences, tutorialCompleted: false }
            : { theme: 'light' as const, notifications: { email: true, push: true }, tutorialCompleted: false }
        }));
      }

      navigate('/dashboard');
      setTimeout(() => {
        startTutorial();
        setIsLoading(false);
      }, 500);
    } catch (error) {
      console.error('Failed to reset tutorial:', error);
      setIsLoading(false);
    }
  }, [navigate, startTutorial, mongoUser, dispatch]);

  // Handle navigation completion - resume tour after page change
  useEffect(() => {
    if (!isRunning) return;
    if (!isNavigatingRef.current) return;
    if (pendingStepIndexRef.current === null) return;

    const pendingIndex = pendingStepIndexRef.current;
    const allSteps = allStepsRef.current;
    if (pendingIndex >= allSteps.length) return;

    const expectedStep = allSteps[pendingIndex];
    const expectedPage = expectedStep.page || 'dashboard';
    const currentPage = getCurrentPage(location.pathname);

    console.log('[Tutorial] Navigation effect:', { currentPage, expectedPage, pendingIndex, isNavigating: isNavigatingRef.current });

    if (currentPage === expectedPage) {
      console.log('[Tutorial] Navigation complete, resuming at step:', pendingIndex);

      // Clear navigation state
      isNavigatingRef.current = false;
      pendingStepIndexRef.current = null;

      // Longer delay for Settings and Calendar pages
      const delay = (currentPage === 'settings' || currentPage === 'calendar') ? 1500 : 500;
      setTimeout(() => {
        createAndStartTour(pendingIndex);
      }, delay);
    }
  }, [location.pathname, isRunning, createAndStartTour]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tourRef.current) {
        tourRef.current.hide();
      }
    };
  }, []);

  const value: TutorialContextValue = {
    isRunning,
    isTutorialCompleted,
    isLoading,
    isTutorialInProgress: isRunning || isNavigatingRef.current,
    startTutorial,
    stopTutorial,
    completeTutorial,
    resetTutorial,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
      <style>{`
        /* Shepherd.js custom theme */
        .shepherd-theme-custom {
          max-width: 500px;
        }

        .shepherd-element {
          background: #ffffff;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        }

        .shepherd-text {
          font-size: 0.9rem;
          line-height: 1.6;
          color: #4a4a4a;
          padding: 1rem;
        }

        .shepherd-text p {
          margin: 0 0 0.5rem 0;
        }

        .shepherd-text p:last-child {
          margin-bottom: 0;
        }

        .shepherd-text b {
          color: #1a1a1a;
          font-weight: 600;
        }

        .shepherd-progress {
          font-size: 0.75rem;
          color: #1976d2;
          font-weight: 500;
          margin-top: 1rem !important;
        }

        .shepherd-header {
          background: #ffffff;
          padding: 1rem 1rem 0 1rem;
          border-radius: 8px 8px 0 0;
        }

        .shepherd-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1a1a1a;
        }

        .shepherd-cancel-icon {
          color: #999;
          font-size: 1.5rem;
        }

        .shepherd-cancel-icon:hover {
          color: #666;
        }

        .shepherd-footer {
          padding: 0.75rem 1rem 1rem 1rem;
          border-top: none;
        }

        .shepherd-button {
          border-radius: 4px;
          padding: 8px 16px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .shepherd-button-secondary {
          background: transparent;
          color: #666;
          border: 1px solid #ddd;
        }

        .shepherd-button-secondary:hover {
          background: #f5f5f5;
        }

        .shepherd-button-secondary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .shepherd-button-primary {
          background: #1976d2;
          color: #ffffff;
          border: none;
        }

        .shepherd-button-primary:hover {
          background: #1565c0;
        }

        .shepherd-modal-overlay-container {
          background: rgba(0, 0, 0, 0.7);
        }

        .shepherd-arrow {
          border-color: #ffffff;
        }

        .shepherd-arrow:before {
          background: #ffffff;
        }
      `}</style>
    </TutorialContext.Provider>
  );
};

export const useTutorialContext = (): TutorialContextValue => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorialContext must be used within a TutorialProvider');
  }
  return context;
};

export default TutorialContext;
